<script setup lang="tsx">
import type { Response, Audit, AuditComment, AddAuditCommentRequest, ListAuditCommentsRequest } from '@/models/api-generated'
import http from '@/services/http'
import { computed, onBeforeMount, ref, nextTick, watch } from 'vue'
import CommentCard from './CommentCard.vue'
import { hasPermission } from '@/services/utils'
import {
	BasicAudit,
	InternalAudit,
	PermBasicAudit,
	PermInternalAudit,
	Ok,
	Remark,
	Defect,
	NoKind,
	InAudit,
	type AuditCommentKind,
} from '@/models/enums-generated'

const props = defineProps({
	methodMetadataId: Number,
})

const model = defineModel<Audit>({ required: true })
const comments = ref<AuditComment[]>([])
const canAddBasic = computed(() => model.value.type === BasicAudit && hasPermission(PermBasicAudit))
const canAddInternal = computed(() => model.value.type === InternalAudit && hasPermission(PermInternalAudit))

const emit = defineEmits(['comment-added'])

const isAddComment = ref(false)
const newComment = ref('')
const commentKind = ref<AuditCommentKind | null>(null)
const commentTextAreaRef = ref<HTMLTextAreaElement | null>(null)

async function fetchComments() {
	comments.value.splice(0, comments.value.length)

	if (!model.value.id) return

	const req: ListAuditCommentsRequest = {
		auditId: model.value.id ?? 0,
		methodMetadataId: props.methodMetadataId ?? 0,
	}
	const { data } = await http.post<Response<AuditComment[]>>('api/audit/listComments', req)
	if (data.data?.length) comments.value.push(...data.data)
}

async function addComment() {
	if (!newComment.value) return

	const req: AddAuditCommentRequest = {
		value: newComment.value,
		auditID: model.value.id ?? 0,
		methodMetadataId: props.methodMetadataId ?? 0,
		kind: commentKind.value ?? NoKind,
	}

	await http.post('api/audit/addComment', req)
	newComment.value = ''
	const addedKind = commentKind.value ?? NoKind
	commentKind.value = null
	isAddComment.value = false
	await fetchComments()
	emit('comment-added', addedKind)
}

function openCommentBoxWithKind(kind: AuditCommentKind | '') {
	commentKind.value = kind || null
	isAddComment.value = true
}

const focusTextArea = () => {
	nextTick(() => {
		if (commentTextAreaRef.value) {
			commentTextAreaRef.value.focus()
		}
	})
}
watch(isAddComment, focusTextArea)

onBeforeMount(fetchComments)
watch(model, fetchComments)

const visibleComments = computed(() => comments.value.filter((c) => c.kind !== InAudit))
</script>
<template>
	<div v-if="visibleComments.length">
		<CommentCard
			v-for="(comment, i) in visibleComments"
			:key="comment.id"
			v-model="comments[i]"
			v-model:audit="model"
			@reply-added="fetchComments"
			@kind-changed="emit('comment-added', $event)"
		></CommentCard>
	</div>
	<div v-else>
		<CommentCard v-model:audit="model"></CommentCard>
	</div>

	<div v-if="model.isActive && (canAddBasic || canAddInternal)">
		<div v-if="isAddComment">
			<div class="position-relative d-block">
				<textarea
					:id="'add-comment-input' + model.id"
					ref="commentTextAreaRef"
					v-model="newComment"
					class="form-control mb-2 pe-5"
					rows="3"
					:placeholder="$t('audit.addComment')"
					maxlength="500"
					@keydown.enter="addComment()"
				></textarea>
				<i v-if="commentKind === Ok" class="bi bi-check-circle position-absolute top-0 end-0 me-2 mt-1 fs-5 text-success pe-none z-1"></i>
				<i
					v-else-if="commentKind === Remark"
					class="bi bi-exclamation-circle position-absolute top-0 end-0 me-2 mt-1 fs-5 text-warning pe-none z-1"
				></i>
				<i v-else-if="commentKind === Defect" class="bi bi-ban position-absolute top-0 end-0 me-2 mt-1 fs-5 text-danger pe-none z-1"></i>
				<i v-else class="bi bi-chat-right-text position-absolute top-0 end-0 me-2 mt-1 fs-5 text-primary pe-none z-1"></i>
			</div>
			<div class="text-end">
				<button :id="'save-audit-comment-btn' + model.id" class="btn btn-success btn-sm me-2" @click="addComment">
					{{ $t('misc.save') }}
				</button>
				<button :id="'cancel-audit-comment-btn' + model.id" class="btn btn-secondary btn-sm" @click="isAddComment = false">
					{{ $t('misc.cancel') }}
				</button>
			</div>
		</div>

		<div v-else class="row g-2 py-3 ms-2">
			<div class="col">
				<button
					:id="'add-audit-comment-btn' + model.id"
					class="btn btn-primary btn-sm w-100"
					@click="openCommentBoxWithKind('')"
					v-one-tooltip="$t('misc.addComment')"
				>
					<i class="bi bi-chat-left-text me-1"></i>
					<span>{{ $t('misc.add') }}</span>
				</button>
			</div>

			<div class="col">
				<button
					:id="'ok-audit-cmt-btn' + model.id"
					class="btn btn-success btn-sm w-100"
					@click="openCommentBoxWithKind(Ok)"
					v-one-tooltip="$t('audit.ok')"
				>
					<i class="bi bi-check-circle me-1"></i>
					<span>{{ $t('audit.ok') }}</span>
				</button>
			</div>

			<div class="col">
				<button
					:id="'remark-audit-cmt-btn' + model.id"
					class="btn btn-warning btn-sm w-100"
					@click="openCommentBoxWithKind(Remark)"
					v-one-tooltip="$t('audit.remark')"
				>
					<i class="bi bi-exclamation-circle me-1"></i>
					<span>{{ $t('audit.remark') }}</span>
				</button>
			</div>

			<div class="col">
				<button
					:id="'defect-audit-cmt-btn' + model.id"
					class="btn btn-danger btn-sm w-100"
					@click="openCommentBoxWithKind(Defect)"
					v-one-tooltip="$t('audit.defect')"
				>
					<i class="bi bi-ban me-1"></i>
					<span>{{ $t('audit.defect') }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped lang="sass">
.btn

	&:hover
		transform: translateY(-2px)
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)

	i
		transition: transform 0.2s ease

	&:hover i
		transform: scale(1.15)

.btn-primary:hover
	filter: brightness(1.1)

.btn-success:hover
	filter: brightness(1.1)

.btn-warning:hover
	filter: brightness(1.1)

.btn-danger:hover
	filter: brightness(1.1)
</style>
